import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { AudienceListType, FormSegment } from '../../../../../../../../modules/audiences/AudienceForm/types.ts';
import { ListType } from '../../../../../../../../modules/audiences/AudienceForm/types.ts';
import type { AgeType } from '../../../hooks/useDemographicsOptions.ts';

import { DemographicsAgeSegmentsListItem } from './DemographicsAgeSegmentsListItem.tsx';
import { segmentsToRangeGroups } from './demographicsUtils.ts';

type BaseDemographicsProps = {
  listType: AudienceListType;
  ageSegments: FormSegment[] | undefined;
};

type DemographicsProps =
  | (BaseDemographicsProps & { readOnly: true })
  | (BaseDemographicsProps & {
      readOnly?: false;
      onAgeDelete: (segment: AgeType) => void;
    });

export const DemographicsAgeSegmentsList = ({ listType, ageSegments, ...rest }: DemographicsProps) => {
  const { t } = useTranslation();
  const countAllSegments = useMemo(() => segmentsToRangeGroups(ageSegments).length - 1, [ageSegments]);

  if (!ageSegments?.length) {
    return null;
  }

  const listTypeLabel = listType === 'includes' ? t('included') : t('excluded');

  const readOnly = rest.readOnly === true;
  const onAgeDelete = !readOnly && 'onAgeDelete' in rest ? rest.onAgeDelete : undefined;

  return (
    <>
      <Typography
        variant="overline"
        sx={{
          color: 'text.secondary',
        }}
      >
        {listTypeLabel}
      </Typography>
      <Grid container>
        <Grid sx={{ mr: 1.5, display: 'flex', alignItems: 'center' }}>
          {listType === ListType.INCLUDES ? (
            <CheckCircleOutlinedIcon data-test="check-icon" color="success" fontSize="small" />
          ) : (
            <BlockOutlinedIcon data-test="block-icon" color="error" fontSize="small" />
          )}
        </Grid>

        {segmentsToRangeGroups(ageSegments).map((range, index) => (
          <Grid key={range.min}>
            <DemographicsAgeSegmentsListItem
              range={range}
              onAgeDelete={onAgeDelete}
              isLastItem={index === countAllSegments}
            />
          </Grid>
        ))}
      </Grid>
    </>
  );
};
